import React, { Children, cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'

import {
	CalendarLine,
	CloseIcon,
	DeleteLine,
	DownloadIcon,
	DuplicateLine,
	EditIcon,
	Input,
	SearchIcon,
} from '../rewards-ui'
import cssVariables from '../../styles/cssVariables'

export { Input }

const color = cssVariables.colors
const spacing = cssVariables.spacings
const radius = cssVariables.borderRadiuses

const getButtonType = type => (['button', 'submit', 'reset'].includes(type) ? type : 'button')

const ButtonBase = styled.button`
	display: ${({ block }) => (block ? 'flex' : 'inline-flex')};
	width: ${({ block }) => (block ? '100%' : 'auto')};
	align-items: center;
	justify-content: center;
	gap: 8px;
	border: 1px solid transparent;
	border-radius: ${radius.default};
	padding: ${({ sm }) => (sm ? '7px 12px' : '10px 18px')};
	font: inherit;
	font-size: ${({ sm }) => (sm ? '13px' : '14px')};
	font-weight: 600;
	line-height: 1.2;
	cursor: pointer;
	color: #fff;
	background: ${color.brand.secondary};
	transition:
		background 120ms ease,
		border-color 120ms ease,
		color 120ms ease,
		box-shadow 120ms ease;

	&:hover {
		background: #241b55;
	}

	&:focus-visible {
		outline: 0;
		box-shadow: 0 0 0 3px rgba(52, 40, 112, 0.22);
	}

	${({ fill, tertiary, light, secondary, primary }) =>
		(fill === 'outline' || tertiary || light || secondary || primary === false) &&
		`
			color: ${color.brand.secondary};
			background: #fff;
			border-color: ${color.borders.divider};

			&:hover {
				background: #f7f6fb;
				border-color: ${color.brand.secondary};
			}
		`}

	${({ fill, link, clean }) =>
		(fill === 'text' || link || clean) &&
		`
			color: ${color.brand.secondary};
			background: transparent;
			border-color: transparent;
			padding-left: 4px;
			padding-right: 4px;

			&:hover {
				background: rgba(52, 40, 112, 0.08);
			}
		`}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
`

export const Button = ({ children, label, busy, disabled, type, ...rest }) => (
	<ButtonBase type={getButtonType(type)} disabled={disabled || busy} {...rest}>
		{busy ? 'Loading...' : children || label}
	</ButtonBase>
)

Button.propTypes = {
	children: PropTypes.node,
	label: PropTypes.node,
	busy: PropTypes.bool,
	disabled: PropTypes.bool,
	type: PropTypes.string,
}

const headingSizes = {
	one: '30px',
	two: '22px',
	three: '17px',
}

const HeadingBase = styled.h2`
	margin: 0 0 ${spacing.lg};
	color: ${color.text.primary};
	font-size: ${({ one, two, three }) => (one && headingSizes.one) || (two && headingSizes.two) || (three && headingSizes.three) || '20px'};
	font-weight: 700;
	line-height: 1.2;
`

export const Heading = ({ one, two, three, children, ...rest }) => {
	const tag = one ? 'h1' : two ? 'h2' : three ? 'h3' : 'h2'
	return (
		<HeadingBase as={tag} one={one} two={two} three={three} {...rest}>
			{children}
		</HeadingBase>
	)
}

Heading.propTypes = {
	one: PropTypes.bool,
	two: PropTypes.bool,
	three: PropTypes.bool,
	children: PropTypes.node,
}

const pulse = keyframes`
	0% { transform: scale(0.86); opacity: 0.55; }
	50% { transform: scale(1); opacity: 1; }
	100% { transform: scale(0.86); opacity: 0.55; }
`

const SpinnerWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin: ${({ inline }) => (inline ? '0' : '48px auto')};
	color: ${color.text.secondary};
`

const SpinnerDot = styled.div`
	width: ${({ width, sm }) => width || (sm ? '24px' : '42px')};
	height: ${({ height, sm }) => height || (sm ? '24px' : '42px')};
	border-radius: 999px;
	background: ${color.brand.secondary};
	animation: ${pulse} 900ms ease-in-out infinite;
`

export const Spinner = ({ loadingText, showText, ...rest }) => (
	<SpinnerWrap>
		<SpinnerDot {...rest} />
		{showText && loadingText && <span>{loadingText}</span>}
	</SpinnerWrap>
)

Spinner.propTypes = {
	loadingText: PropTypes.node,
	showText: PropTypes.bool,
}

const alertTone = ({ success, danger, warning, primary }) => {
	if (success) return { bg: '#f0f7ee', border: '#88bc76', text: '#315926' }
	if (danger) return { bg: '#fff1ec', border: '#ef7f43', text: '#7a2d07' }
	if (warning) return { bg: '#fff7dd', border: '#feb734', text: '#6e4700' }
	if (primary) return { bg: '#f0eef9', border: color.brand.secondary, text: color.brand.secondary }
	return { bg: '#eef8fa', border: color.semantic.information, text: '#236876' }
}

const AlertBox = styled.div`
	margin: 0 0 ${spacing.lg};
	padding: ${spacing.md} ${spacing.lg};
	border: 1px solid ${({ tone }) => tone.border};
	border-radius: ${radius.default};
	color: ${({ tone }) => tone.text};
	background: ${({ tone }) => tone.bg};
	line-height: 1.45;
`

export const Alert = ({ children, ...props }) => <AlertBox tone={alertTone(props)} {...props}>{children}</AlertBox>

Alert.propTypes = {
	children: PropTypes.node,
}

const ModalBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: radial-gradient(ellipse at center, rgba(20, 20, 28, 0.22) 0%, rgba(20, 20, 28, 0.08) 100%);
	backdrop-filter: blur(6px);
	-webkit-backdrop-filter: blur(6px);
`

const ModalPanel = styled.div`
	width: min(720px, 100%);
	max-height: calc(100vh - 48px);
	overflow: auto;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 22px 80px rgba(0, 0, 0, 0.24);
`

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 18px 22px;
	border-bottom: 1px solid ${color.borders.divider};
`

const ModalTitle = styled.div`
	font-size: 18px;
	font-weight: 700;
`

const ModalContent = styled.div`
	padding: 22px;
`

const ModalFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 16px 22px 22px;
`

const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: ${({ sm, xs }) => (xs ? '22px' : sm ? '28px' : '34px')};
	height: ${({ sm, xs }) => (xs ? '22px' : sm ? '28px' : '34px')};
	border: 0;
	border-radius: 999px;
	color: currentColor;
	background: transparent;
	cursor: pointer;

	&:hover {
		background: rgba(52, 40, 112, 0.08);
	}
`

export const Modal = ({
	children,
	title,
	open,
	show,
	onClose,
	onOk,
	okText = 'OK',
	okDisabled = false,
	cancelText = 'Cancel',
	showFooter,
	showCloseButton = true,
	...rest
}) => {
	const isOpen = open ?? show ?? true
	if (!isOpen) return null

	return (
		<ModalBackdrop role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose?.()}>
			<ModalPanel role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} {...rest}>
				{(title || showCloseButton) && (
					<ModalHeader>
						<ModalTitle>{title}</ModalTitle>
						{showCloseButton && (
							<IconButton type="button" aria-label="Close" onClick={onClose}>
								x
							</IconButton>
						)}
					</ModalHeader>
				)}
				<ModalContent>{children}</ModalContent>
				{showFooter && (
					<ModalFooter>
						<Button fill="outline" type="button" onClick={onClose}>
							{cancelText}
						</Button>
						<Button type="button" onClick={onOk} disabled={okDisabled}>
							{okText}
						</Button>
					</ModalFooter>
				)}
			</ModalPanel>
		</ModalBackdrop>
	)
}

Modal.propTypes = {
	children: PropTypes.node,
	title: PropTypes.node,
	open: PropTypes.bool,
	show: PropTypes.bool,
	onClose: PropTypes.func,
	onOk: PropTypes.func,
	okText: PropTypes.node,
	okDisabled: PropTypes.bool,
	cancelText: PropTypes.node,
	showFooter: PropTypes.bool,
	showCloseButton: PropTypes.bool,
}

const TooltipWrap = styled.span`
	position: relative;
	display: inline-flex;
	align-items: center;

	&:hover > span,
	&:focus-within > span {
		opacity: 1;
		transform: translateY(-2px);
		pointer-events: auto;
	}
`

const TooltipBubble = styled.span`
	position: absolute;
	z-index: 20;
	bottom: ${({ right }) => (right ? 'auto' : 'calc(100% + 8px)')};
	left: ${({ right }) => (right ? 'calc(100% + 8px)' : '50%')};
	top: ${({ right }) => (right ? '50%' : 'auto')};
	width: max-content;
	max-width: 240px;
	transform: ${({ right }) => (right ? 'translateY(-50%)' : 'translate(-50%, 2px)')};
	padding: 6px 8px;
	border-radius: ${radius.default};
	color: #fff;
	background: #252033;
	font-size: 12px;
	line-height: 1.3;
	opacity: 0;
	pointer-events: none;
	transition:
		opacity 120ms ease,
		transform 120ms ease;
`

export const Tooltip = ({ children, value, right }) => (
	<TooltipWrap tabIndex={value ? 0 : undefined}>
		{children}
		{value && <TooltipBubble right={right}>{value}</TooltipBubble>}
	</TooltipWrap>
)

Tooltip.propTypes = {
	children: PropTypes.node,
	value: PropTypes.node,
	right: PropTypes.bool,
}

const iconMap = {
	trash: DeleteLine,
	delete: DeleteLine,
	download: DownloadIcon,
	duplicate: DuplicateLine,
	edit: EditIcon,
	search: SearchIcon,
	close: CloseIcon,
	exit: CloseIcon,
	calendar: CalendarLine,
	home: HomeIcon,
	merchant: StoreIcon,
	campaigns: CampaignIcon,
	reports: ReportsIcon,
	card: CardIcon,
	rule: SettingsIcon,
	cashback: FinanceIcon,
	user: UserIcon,
	logout: LogoutIcon,
	down: ChevronDownIcon,
	up: ChevronUpIcon,
	reset: ResetIcon,
	info: InfoIcon,
}

export const Icon = ({ name, onClick, sm, xs, lg, className, round, fill, primary, clickUi, ...rest }) => {
	const IconComponent = iconMap[name]
	const size = xs ? 14 : sm ? 21 : lg ? 28 : 22
	const content = IconComponent ? <IconComponent width={size} height={size} /> : <IconFallback>{iconGlyph(name)}</IconFallback>
	const iconProps = { $size: size, $round: round, $fill: fill, $primary: primary }

	if (onClick) {
		return (
			<IconButton type="button" onClick={onClick} sm={sm} xs={xs} className={className} {...iconProps} {...rest}>
				{content}
			</IconButton>
		)
	}

	return (
		<IconWrap className={className} {...iconProps} {...rest}>
			{content}
		</IconWrap>
	)
}

Icon.propTypes = {
	name: PropTypes.string,
	onClick: PropTypes.func,
	sm: PropTypes.bool,
	xs: PropTypes.bool,
	lg: PropTypes.bool,
	className: PropTypes.string,
}

const iconGlyph = name =>
	({
		home: 'H',
		merchant: 'M',
		campaigns: 'C',
		reports: 'R',
		card: '$',
		rule: '#',
		cashback: '$',
		user: 'U',
		down: 'v',
		up: '^',
		reset: 'R',
	}[name] || '•')

const IconWrap = styled.span`
	display: inline-flex;
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	flex: 0 0 ${({ $size }) => $size}px;
	align-items: center;
	justify-content: center;
	border-radius: ${({ $round }) => ($round ? '999px' : 0)};
	color: ${({ $fill }) => ($fill ? '#fff' : 'currentColor')};
	background: ${({ $fill, $primary }) => ($fill && $primary ? color.brand.secondary : 'transparent')};

	svg {
		display: block;
	}
`

const IconFallback = styled.span`
	font-size: 12px;
	font-weight: 700;
	line-height: 1;
`

const SvgIcon = ({ children, width = 20, height = 20 }) => (
	<svg
		width={width}
		height={height}
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
			{children}
		</g>
	</svg>
)

SvgIcon.propTypes = {
	children: PropTypes.node,
	width: PropTypes.number,
	height: PropTypes.number,
}

function HomeIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M3.5 11.2 12 4l8.5 7.2" />
		<path d="M5.5 10.4v8.1h4.2v-5.2h4.6v5.2h4.2v-8.1" />
	</SvgIcon>
	)
}

function StoreIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M5 10.2v8.3h14v-8.3" />
		<path d="M4.4 5.5h15.2l1 4.7H3.4l1-4.7Z" />
		<path d="M7.4 10.2v-4.7" />
		<path d="M12 10.2v-4.7" />
		<path d="M16.6 10.2v-4.7" />
		<path d="M9 18.5v-4h6v4" />
	</SvgIcon>
	)
}

function CampaignIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M4 13.2h3.2l8.4 3.8V7l-8.4 3.8H4v2.4Z" />
		<path d="M7.2 13.2 9 18" />
		<path d="M18.2 9.5c.8.6 1.3 1.5 1.3 2.5s-.5 1.9-1.3 2.5" />
	</SvgIcon>
	)
}

function ReportsIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M4 19.5h16" />
		<path d="M6.5 16v-4" />
		<path d="M12 16V7" />
		<path d="M17.5 16v-6" />
		<path d="m5 9.8 4-3.4 4 2.8 5-5" />
	</SvgIcon>
	)
}

function CardIcon(props) {
	return (
	<SvgIcon {...props}>
		<rect x="3.5" y="6.5" width="17" height="11" rx="1.6" />
		<path d="M3.5 10h17" />
		<path d="M7 14.3h4" />
	</SvgIcon>
	)
}

function SettingsIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M12 15.3a3.3 3.3 0 1 0 0-6.6 3.3 3.3 0 0 0 0 6.6Z" />
		<path d="m18.8 13.7 1.2.9-1.8 3.1-1.4-.6a7 7 0 0 1-1.6.9L15 19.5H9l-.2-1.5a7 7 0 0 1-1.6-.9l-1.4.6L4 14.6l1.2-.9a7.4 7.4 0 0 1 0-3.4L4 9.4l1.8-3.1 1.4.6A7 7 0 0 1 8.8 6L9 4.5h6l.2 1.5a7 7 0 0 1 1.6.9l1.4-.6L20 9.4l-1.2.9a7.4 7.4 0 0 1 0 3.4Z" />
	</SvgIcon>
	)
}

function FinanceIcon(props) {
	return (
	<SvgIcon {...props}>
		<rect x="3.5" y="7" width="17" height="10" rx="1.4" />
		<path d="M7 10.2h3.5" />
		<path d="M7 13.8h2" />
		<circle cx="16" cy="12" r="2" />
	</SvgIcon>
	)
}

function UserIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M12 12.2a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
		<path d="M4.5 20c1.1-3.4 3.5-5.1 7.5-5.1s6.4 1.7 7.5 5.1" />
	</SvgIcon>
	)
}

function LogoutIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M10 5H5.5v14H10" />
		<path d="M13.5 8.5 17 12l-3.5 3.5" />
		<path d="M17 12H8" />
	</SvgIcon>
	)
}

function ChevronDownIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="m7 9.5 5 5 5-5" />
	</SvgIcon>
	)
}

function ChevronUpIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="m7 14.5 5-5 5 5" />
	</SvgIcon>
	)
}

function ResetIcon(props) {
	return (
	<SvgIcon {...props}>
		<path d="M5 12a7 7 0 1 0 2.1-5" />
		<path d="M5 5.5v5h5" />
	</SvgIcon>
	)
}

function InfoIcon(props) {
	return (
	<SvgIcon {...props}>
		<circle cx="12" cy="12" r="8.2" />
		<path d="M12 10.8v5.2" />
		<path d="M12 7.8h.01" />
	</SvgIcon>
	)
}

export const Grid = styled.div`
	width: 100%;
`

export const Row = styled.div`
	display: grid;
	grid-template-columns: repeat(12, minmax(0, 1fr));
	gap: ${spacing.md};
	margin-bottom: ${spacing.md};
`

const columnSpan = value => {
	const numeric = Number(value)
	if (!numeric) return 12
	return Math.min(Math.max(numeric, 1), 12)
}

export const Column = styled.div`
	grid-column: span ${({ size }) => columnSpan(size)};
	min-width: 0;

	@media (max-width: 1023px) {
		grid-column: span ${({ tablet, size }) => columnSpan(tablet || size)};
	}

	@media (max-width: 767px) {
		grid-column: span ${({ mobile, phablet, tablet }) => columnSpan(mobile || phablet || tablet || 12)};
	}
`

const CheckboxLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`

export const Checkbox = ({ label, checked, initialValue, onChange, disabled, ...rest }) => {
	const [innerChecked, setInnerChecked] = useState(Boolean(checked ?? initialValue))
	const isControlled = checked !== undefined
	const value = isControlled ? checked : innerChecked

	useEffect(() => {
		if (!isControlled) setInnerChecked(Boolean(initialValue))
	}, [initialValue, isControlled])

	const handleChange = event => {
		if (!isControlled) setInnerChecked(event.target.checked)
		onChange?.(event.target.checked, event)
	}

	return (
		<CheckboxLabel disabled={disabled} {...rest}>
			<input type="checkbox" checked={Boolean(value)} disabled={disabled} onChange={handleChange} />
			{label}
		</CheckboxLabel>
	)
}

Checkbox.propTypes = {
	label: PropTypes.node,
	checked: PropTypes.bool,
	initialValue: PropTypes.bool,
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
}

const SwitchTrack = styled.button`
	width: 42px;
	height: 24px;
	border: 0;
	border-radius: 999px;
	padding: 3px;
	background: ${({ checked }) => (checked ? color.brand.secondary : color.semantic.disabled)};
	cursor: pointer;
	transition: background 120ms ease;

	&::before {
		content: '';
		display: block;
		width: 18px;
		height: 18px;
		border-radius: 999px;
		background: #fff;
		transform: translateX(${({ checked }) => (checked ? '18px' : '0')});
		transition: transform 120ms ease;
	}
`

export const Switch = ({ checked, onChange, disabled }) => (
	<SwitchTrack type="button" checked={checked} disabled={disabled} onClick={() => !disabled && onChange?.(!checked)} />
)

Switch.propTypes = {
	checked: PropTypes.bool,
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
}

const SelectBase = styled.select`
	width: 100%;
	border: 1px solid ${color.borders.default};
	border-radius: ${radius.default};
	padding: 10px 12px;
	background: #fff;
	font: inherit;
`

export const Select = ({ items = [], children, onChange, value, ...rest }) => (
	<SelectBase value={value} onChange={event => onChange?.(event.target.value, event)} {...rest}>
		{children ||
			items.map(item => (
				<option key={item.value ?? item.id ?? item.label} value={item.value ?? item.id}>
					{item.label ?? item.name ?? item.value}
				</option>
			))}
	</SelectBase>
)

Select.propTypes = {
	items: PropTypes.array,
	children: PropTypes.node,
	onChange: PropTypes.func,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const RadioGroup = styled.div`
	display: flex;
	flex-wrap: ${({ vertical }) => (vertical ? 'nowrap' : 'wrap')};
	flex-direction: ${({ vertical }) => (vertical ? 'column' : 'row')};
	gap: 10px 18px;
`

export const RadioList = ({ items = [], initialValue, value, onChange, disabled }) => {
	const [selected, setSelected] = useState(value ?? initialValue)
	const current = value ?? selected

	useEffect(() => {
		if (value === undefined) setSelected(initialValue)
	}, [initialValue, value])

	return (
		<RadioGroup>
			{items.map(item => (
				<CheckboxLabel key={item.value} disabled={disabled}>
					<input
						type="radio"
						checked={current === item.value}
						disabled={disabled}
						onChange={() => {
							if (value === undefined) setSelected(item.value)
							onChange?.(item.value)
						}}
					/>
					{item.label}
				</CheckboxLabel>
			))}
		</RadioGroup>
	)
}

RadioList.propTypes = {
	items: PropTypes.array,
	initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
}

const PillWrap = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`

const PillButton = styled.button`
	border: 1px solid ${({ selected }) => (selected ? color.brand.secondary : color.borders.divider)};
	border-radius: 999px;
	padding: ${({ sm }) => (sm ? '5px 10px' : '7px 12px')};
	color: ${({ selected }) => (selected ? '#fff' : color.text.primary)};
	background: ${({ selected }) => (selected ? color.brand.secondary : '#fff')};
	cursor: pointer;
`

export const PillList = ({ items, value, onSelect, sm, children, ...rest }) => (
	<PillWrap {...rest}>
		{children ||
			items?.map(item => {
				const itemValue = item.value ?? item.id ?? item
				return (
					<PillButton key={String(itemValue)} sm={sm} selected={value === itemValue} onClick={() => onSelect?.(itemValue)}>
						{item.label ?? item.name ?? itemValue}
					</PillButton>
				)
			})}
	</PillWrap>
)

PillList.propTypes = {
	items: PropTypes.array,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onSelect: PropTypes.func,
	sm: PropTypes.bool,
	children: PropTypes.node,
}

const TabNav = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 24px;
	border-bottom: 1px solid ${color.borders.divider};
`

const TabButton = styled.button`
	border: 0;
	border-bottom: 3px solid ${({ active }) => (active ? color.brand.secondary : 'transparent')};
	padding: 11px 14px;
	color: ${({ active }) => (active ? color.brand.secondary : color.text.secondary)};
	background: transparent;
	font: inherit;
	font-weight: 700;
	cursor: pointer;
`

export const TabPane = ({ children }) => children

TabPane.propTypes = {
	children: PropTypes.node,
	title: PropTypes.node,
	id: PropTypes.string,
}

export const Tab = ({ children, defaultOpen, onSelect }) => {
	const panes = useMemo(() => Children.toArray(children).filter(isValidElement), [children])
	const initial = panes.findIndex(pane => pane.props.id === defaultOpen)
	const [active, setActive] = useState(initial >= 0 ? initial : 0)

	useEffect(() => {
		const next = panes.findIndex(pane => pane.props.id === defaultOpen)
		if (next >= 0) setActive(next)
	}, [defaultOpen, panes])

	const activePane = panes[active]

	return (
		<div>
			<TabNav>
				{panes.map((pane, index) => (
					<TabButton
						key={pane.props.id || index}
						type="button"
						active={index === active}
						onClick={() => {
							setActive(index)
							onSelect?.(pane.props.id)
						}}
					>
						{pane.props.title}
					</TabButton>
				))}
			</TabNav>
			<div>{activePane}</div>
		</div>
	)
}

Tab.propTypes = {
	children: PropTypes.node,
	defaultOpen: PropTypes.string,
	onSelect: PropTypes.func,
}

export const Card = styled.div`
	border: 1px solid ${color.borders.divider};
	border-radius: 8px;
	padding: 24px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(20, 20, 28, 0.04);
`

export const Text = styled.div`
	color: ${color.text.secondary};
	font-size: 14px;
	line-height: 1.4;
`

const Sidebar = styled.aside`
	position: fixed;
	inset: 0 auto 0 0;
	z-index: 30;
	width: 220px;
	display: flex;
	flex-direction: column;
	border-right: 1px solid ${color.borders.divider};
	background: #fff;

	@media (max-width: 1023px) {
		width: 72px;
	}

	@media (max-width: 767px) {
		inset: 0 auto 0 0;
		width: 56px;
		height: auto;
		flex-direction: column;
		align-items: stretch;
		border-top: 1px solid ${color.borders.divider};
		border-right: 0;
		overflow-y: auto;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}
`

export const AppSidebar = ({ children, ...rest }) => <Sidebar {...rest}>{children}</Sidebar>

AppSidebar.propTypes = {
	children: PropTypes.node,
}

const HeaderSection = styled.div`
	padding: ${({ bottom }) => (bottom ? '12px' : '20px 16px')};
	margin-top: ${({ bottom }) => (bottom ? 'auto' : '0')};
	border-top: ${({ bottom }) => (bottom ? `1px solid ${color.borders.divider}` : '0')};

	@media (max-width: 1023px) {
		padding: ${({ bottom }) => (bottom ? '10px' : '14px 10px')};
	}

	@media (max-width: 767px) {
		display: flex;
		flex: ${({ bottom }) => (bottom ? '0 0 auto' : '0 0 auto')};
		flex-direction: column;
		align-items: center;
		padding: ${({ bottom }) => (bottom ? '8px 6px' : '10px 6px')};
		margin-top: ${({ bottom }) => (bottom ? 'auto' : '0')};
		border-top: 0;
	}
`

const LogoButton = styled.button`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: center;
	border: 0;
	background: transparent;
	cursor: pointer;

	@media (max-width: 767px) {
		width: 44px;
		height: 44px;
		flex: 0 0 44px;
	}
`

export const AppHeader = ({ children, imageUrl, imageSvg, imageHeight = 42, imageWidth = 120, onClick, bottom, ...rest }) => (
	<HeaderSection className={bottom ? 'AppHeader AppHeader--bottom' : 'AppHeader'} bottom={bottom} {...rest}>
		{(imageUrl || imageSvg) && (
			<LogoButton type="button" onClick={onClick} aria-label="Home">
				{imageUrl ? (
					<img src={imageUrl} height={imageHeight} width={imageWidth} alt="" />
				) : (
					<span dangerouslySetInnerHTML={{ __html: imageSvg }} />
				)}
			</LogoButton>
		)}
		{children}
	</HeaderSection>
)

AppHeader.propTypes = {
	children: PropTypes.node,
	imageUrl: PropTypes.string,
	imageSvg: PropTypes.string,
	imageHeight: PropTypes.number,
	imageWidth: PropTypes.number,
	onClick: PropTypes.func,
	bottom: PropTypes.bool,
}

export const AppNavigation = styled.nav.attrs({ className: 'AppNavigation' })`
	margin-top: 22px;

	@media (max-width: 767px) {
		margin-top: 0;
	}
`

export const Nav = styled.div`
	display: flex;
	flex-direction: ${({ vertical }) => (vertical ? 'column' : 'row')};
	gap: 2px;

	@media (max-width: 767px) {
		flex-direction: column;
		gap: 4px;
	}
`

const NavButton = styled.button`
	display: ${({ visible }) => (visible === false ? 'none' : 'flex')};
	width: 100%;
	align-items: center;
	gap: 10px;
	border: 0;
	border-radius: 6px;
	padding: 13px 16px;
	color: ${({ selected }) => (selected ? color.brand.secondary : 'rgba(0, 0, 0, 0.60)')};
	background: ${({ selected }) => (selected ? 'rgba(52, 40, 112, 0.1)' : 'transparent')};
	font: inherit;
	font-weight: ${({ selected }) => (selected ? 700 : 600)};
	text-align: left;
	cursor: pointer;

	&:hover {
		background: rgba(52, 40, 112, 0.07);
		color: ${color.brand.secondary};
	}

	@media (max-width: 1023px) {
		justify-content: center;
		gap: 0;
		padding: 10px;
	}

	@media (max-width: 767px) {
		width: 44px;
		height: 44px;
		flex: 0 0 44px;
		padding: 0;
	}
`

export const NavLink = ({ label, icon, ...rest }) => (
	<NavButton type="button" {...rest}>
		<Icon name={icon} sm />
		<span className="Nav-link">{label}</span>
	</NavButton>
)

NavLink.propTypes = {
	label: PropTypes.node,
	icon: PropTypes.string,
}

export const AppLogout = ({ logoutText, onClick, ...rest }) => (
	<NavButton type="button" onClick={onClick} {...rest}>
		<Icon name="logout" sm />
		<span className="AppLogout-text">{logoutText}</span>
	</NavButton>
)

AppLogout.propTypes = {
	logoutText: PropTypes.node,
	onClick: PropTypes.func,
}

export const OverlayProvider = ({ outletId }) => <div id={outletId} />

OverlayProvider.propTypes = {
	outletId: PropTypes.string,
}

const toDateInputValue = value => {
	if (!value) return ''
	const date = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toISOString().slice(0, 10)
}

export const DatePicker = ({ children, defaultDate, onChange, disabled, ...rest }) => {
	const inputRef = useRef(null)
	const child = Children.only(children)

	const openPicker = event => {
		child.props.onClick?.(event)
		if (!disabled) inputRef.current?.showPicker?.()
		if (!disabled && !inputRef.current?.showPicker) inputRef.current?.focus()
	}

	return (
		<span style={{ position: 'relative', display: 'inline-block' }} {...rest}>
			{cloneElement(child, { onClick: openPicker, readOnly: true })}
			<input
				ref={inputRef}
				type="date"
				disabled={disabled}
				value={toDateInputValue(defaultDate)}
				onChange={event => onChange?.(event.target.value)}
				style={{ position: 'absolute', right: 0, bottom: 0, width: 1, height: 1, opacity: 0 }}
			/>
		</span>
	)
}

DatePicker.propTypes = {
	children: PropTypes.node,
	defaultDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
}

const RangePickerWrap = styled.div`
	display: flex;
	gap: 8px;
	padding: 12px;
	border: 1px solid ${color.borders.divider};
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
`

export const DateRangePicker = ({ onChange, disabled, onOutsideClick, ...rest }) => {
	const [from, setFrom] = useState('')
	const [to, setTo] = useState('')

	useEffect(() => {
		const handleDown = event => onOutsideClick?.(event)
		document.addEventListener('mousedown', handleDown)
		return () => document.removeEventListener('mousedown', handleDown)
	}, [onOutsideClick])

	useEffect(() => {
		if (from && to) onChange?.({ from, to })
	}, [from, to, onChange])

	return (
		<RangePickerWrap {...rest}>
			<input type="date" disabled={disabled} value={from} onChange={event => setFrom(event.target.value)} />
			<input type="date" disabled={disabled} value={to} onChange={event => setTo(event.target.value)} />
		</RangePickerWrap>
	)
}

DateRangePicker.propTypes = {
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
	onOutsideClick: PropTypes.func,
}

export const ContextMenu = ({ children }) => <div>{children}</div>

ContextMenu.propTypes = {
	children: PropTypes.node,
}
